if (!customElements.get("carousel-custom")) {
  class CarouselCustom extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideContainer: ".carousel-custom-track",
        slideWrapper: ".carousel-custom-slides",
        slides: ".carousel-custom-slide",
        prevButton: ".carousel-nav-prev",
        nextButton: ".carousel-nav-next",
        pagination: ".carousel-custom-pagination"
      };

      this.classes = {
        active: 'active',
        swiper: 'swiper-container',
        swiperWrapper: 'swiper-wrapper'
      };

      this.slider = null;
      this.currentSlide = 0;

      // Get data attributes
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5;
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.total = parseInt(this.dataset.total) || 0;
      this.expanded = this.dataset.expanded === "true";
      this.itemsPerRow = parseInt(this.dataset.itemsPerRow) || 3;

      this.init();
    }

    init() {
      this.initSlider();
    }

    initSlider() {
      const slideContainer = this.querySelector(this.selectors.slideContainer);
      const prevButton = this.querySelector(this.selectors.prevButton);
      const nextButton = this.querySelector(this.selectors.nextButton);

      if (!slideContainer) return;

      // Swiper configuration
      this.options = {
        slidesPerView: 2,
        spaceBetween: 20,
        loop: this.total > 1,
        autoplay: this.autoplay ? {
          delay: this.autoplaySpeed * 1000,
          disableOnInteraction: false,
        } : false,
        pagination: this.showPagination ? {
          el: this.querySelector(this.selectors.pagination),
          clickable: true,
          bulletClass: "carousel-dot",
          bulletActiveClass: "carousel-dot-active",
        } : false,
        navigation: (prevButton && nextButton) ? {
          nextEl: nextButton,
          prevEl: prevButton,
        } : false,
        breakpoints: {
          1024: {
            slidesPerView: this.expanded && this.total > this.itemsPerRow ? this.itemsPerRow + 0.5 : this.itemsPerRow,
            spaceBetween: 40,
          },
          768: {
            slidesPerView: 3,
            spaceBetween: 30,
          },
        },
        on: {
          init: () => {
            this.updateActiveStates();
          },
          slideChange: () => {
            this.updateActiveStates();
          }
        }
      };

      // Initialize Swiper if available
      if (typeof Swiper !== 'undefined') {
        this.slider = new Swiper(slideContainer, this.options);
      } else if (typeof MinimogLibs !== 'undefined' && MinimogLibs.Swiper) {
        this.slider = new MinimogLibs.Swiper(slideContainer, this.options);
      } else {
        // Fallback: Simple manual carousel
        this.initFallbackCarousel();
      }
    }

    initFallbackCarousel() {
      const slideWrapper = this.querySelector(this.selectors.slideWrapper);
      const slides = this.querySelectorAll(this.selectors.slides);
      const prevButton = this.querySelector(this.selectors.prevButton);
      const nextButton = this.querySelector(this.selectors.nextButton);

      if (!slideWrapper || slides.length === 0) return;

      // Set up manual navigation
      if (prevButton) {
        prevButton.addEventListener('click', () => this.prevSlide());
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => this.nextSlide());
      }

      // Set up pagination dots
      if (this.showPagination) {
        this.createPaginationDots();
      }

      // Set up autoplay
      if (this.autoplay) {
        this.startAutoplay();
      }

      // Initialize first slide
      this.updateSlidePosition();
      this.updateActiveStates();
    }

    createPaginationDots() {
      const paginationContainer = this.querySelector(this.selectors.pagination);
      if (!paginationContainer) return;

      const slides = this.querySelectorAll(this.selectors.slides);
      paginationContainer.innerHTML = '';

      slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.classList.add('carousel-dot');
        if (index === 0) dot.classList.add('carousel-dot-active');
        dot.addEventListener('click', () => this.goToSlide(index));
        paginationContainer.appendChild(dot);
      });
    }

    nextSlide() {
      const slides = this.querySelectorAll(this.selectors.slides);
      this.currentSlide = (this.currentSlide + 1) % slides.length;
      this.updateSlidePosition();
      this.updateActiveStates();
    }

    prevSlide() {
      const slides = this.querySelectorAll(this.selectors.slides);
      this.currentSlide = this.currentSlide === 0 ? slides.length - 1 : this.currentSlide - 1;
      this.updateSlidePosition();
      this.updateActiveStates();
    }

    goToSlide(index) {
      this.currentSlide = index;
      this.updateSlidePosition();
      this.updateActiveStates();
    }

    updateSlidePosition() {
      const slideWrapper = this.querySelector(this.selectors.slideWrapper);
      if (slideWrapper) {
        const translateX = -this.currentSlide * 100;
        slideWrapper.style.transform = `translateX(${translateX}%)`;
      }
    }

    updateActiveStates() {
      // Update pagination dots
      const dots = this.querySelectorAll('.carousel-dot');
      dots.forEach((dot, index) => {
        dot.classList.toggle('carousel-dot-active', index === this.currentSlide);
      });

      // Update slides
      const slides = this.querySelectorAll(this.selectors.slides);
      slides.forEach((slide, index) => {
        slide.classList.toggle(this.classes.active, index === this.currentSlide);
      });
    }

    startAutoplay() {
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, this.autoplaySpeed * 1000);
    }

    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.slider && this.slider.destroy) {
        this.slider.destroy();
      }
    }
  }

  customElements.define("carousel-custom", CarouselCustom);
}
